/**
 * Invoicing-tab charge helpers.
 *
 * The operational charge RULES used to live here — a client-side table deciding when
 * hazmat, bobtail, chassis split, waiting time or pre-pull was owed. They now live in
 * the backend reconciler (`invoice_reconciler/charges.py`), which is the only place
 * that can also see the DB-derived charges (chassis, fuel) and the only place that can
 * feed the apply path. One engine, both surfaces.
 *
 * What remains here is presentation and the LIVE suppression: the server decides what
 * is owed and at what rate (one call per load), while the client keeps checking that
 * answer against the rows on screen — including unsaved ones — so a charge the user
 * just added stops being flagged immediately, with no round trip per keystroke.
 *
 * The quote-vs-invoice RATE comparisons below stay client-side on purpose: they're pure
 * comparisons over data the tab already holds, so moving them would buy nothing.
 */
import type { EstimatedLine, InvoiceEstimate } from '../../../services/invoiceReconcile.service';

/** A charge the engine says this order has earned but the invoice doesn't carry. */
export interface ChargeSuggestion {
  /** The invoice line-item name (`HAZARDOUS`, `YARD STORAGE`, …) — also the identity
   *  used for the already-on-the-invoice check. */
  item: string;
  /** 'add' — the charge is missing from the invoice.
   *  'update' — the charge is there but its figures differ.
   *  'remove' — the charge is on the invoice but nothing the engine knows earns it.
   *  The three read very differently to a user, so the banner must not conflate them. */
  kind: 'add' | 'update' | 'remove';
  /** Why it's being suggested, in the engine's words. */
  reason: string;
  /** The record behind the reason (a Custom Chassis Rule, say), linked as the Invoice
   *  Reconciliation modal links it. '' / 0 when the reason names none. */
  reasonLinkTable: string;
  reasonLinkRecno: number;
  /** The invoice line a `remove` is about. */
  existingRecno?: number;
  /** The quote charge the rate came from, so the UI can highlight that row and hang
   *  an inline "+ Add to Invoice" action off it. Absent for derived charges like
   *  chassis and fuel, which have no quote-charge row. */
  sourceChargeId?: number;
  /** The figures are projected from today because the order isn't fully dated yet. */
  estimated: boolean;
  estimateBasis: string;
  rate: number;
  qty: number;
  total: number;
}

const normalizeName = (s: string | null | undefined): string => (s ?? '').trim().toUpperCase();

/**
 * Turn an Invoice Estimate into the tab's callouts, suppressing anything the rows on
 * screen already satisfy.
 *
 * An `add` claims "this charge isn't on the invoice", so a matching row — saved or not —
 * settles it. A `remove` claims the opposite of one line, so it is settled once that line
 * is gone from the grid: matched by its recno, because two lines can share a type and
 * only one is the target (by name only for a line with no recno). An `update` claims "the
 * value on this line is wrong", which neither presence nor absence answers, so those
 * stand until the next estimate is fetched.
 *
 * Removals are callouts like the others (Lee, 2026-10-23), for ANY line type: the modal
 * shows them, and a tab that shows the adds but not the removes reads as the whole story.
 * `held` never becomes a callout — an overridden line is one someone has deliberately
 * settled, and it has its own row among the overrides.
 */
export function suggestionsFromEstimate(
  estimate: InvoiceEstimate | null | undefined,
  lineItems: ReadonlyArray<{ item?: string | null; recno?: number | null }>,
): ChargeSuggestion[] {
  // `lines` is defaulted rather than assumed: a callout is advisory, and a partial or
  // failed response must never take the Invoicing tab down with it.
  const lines = (estimate?.lines ?? []) as ReadonlyArray<EstimatedLine>;
  if (!lines.length) return [];
  // A BILLED ACCESSORIAL's rows are the reconciliation frozen when it was invoiced. Its
  // suggestions are history — nothing on it can change — so they are no callout on this
  // tab, which edits the main invoice. The main invoice's own frozen rows still are when
  // it is the one billed: that is what the tab has shown a billed order since #33848.
  const billedAccessorials = new Set(
    (estimate?.invoices ?? [])
      .filter((p) => p.invoiced && !p.isMain && p.invoiceRecno != null)
      .map((p) => p.invoiceRecno),
  );
  const present = new Set(lineItems.map((li) => normalizeName(li.item)).filter(Boolean));
  const presentRecnos = new Set(
    lineItems.map((li) => li.recno).filter((r): r is number => typeof r === 'number' && r > 0),
  );
  const out: ChargeSuggestion[] = [];
  for (const line of lines) {
    if (line.source !== 'add' && line.source !== 'update' && line.source !== 'remove') continue;
    if (line.invoiceRecno != null && billedAccessorials.has(line.invoiceRecno)) continue;
    if (line.source === 'add' && present.has(normalizeName(line.item))) continue;
    if (line.source === 'remove' && (line.existingRecno
      ? !presentRecnos.has(line.existingRecno)
      : !present.has(normalizeName(line.item)))) continue;
    out.push({
      item: line.item,
      kind: line.source,
      reason: line.reason,
      reasonLinkTable: line.reasonLinkTable ?? '',
      reasonLinkRecno: line.reasonLinkRecno ?? 0,
      existingRecno: line.existingRecno ?? undefined,
      sourceChargeId: line.sourceChargeRecno ?? undefined,
      estimated: line.estimated,
      estimateBasis: line.estimateBasis,
      rate: line.rate,
      qty: line.qty,
      total: line.total,
    });
  }
  return out;
}

export interface DrayRateMismatch {
  /** The base-dray invoice line item's rate. */
  invoiceRate: number;
  /** The linked quote's dray rate. */
  quoteRate: number;
}

/** Base drayage line item: contains "DRAY" but not ADDITIONAL / TRANSLOAD
 *  (per prod data, chiefly "CONTAINER DRAY"). */
const DRAY_RE = /DRAY/i;
const DRAY_EXCLUDE_RE = /ADDITIONAL|TRANSLOAD/i;

/**
 * The invoice's base drayage line item, or undefined. Also used to tell the FSC
 * preview which rate column the reconciler will pick (its DIRECT arm keys off
 * the dray line's `liitem`).
 */
export function findBaseDrayItem<T extends { item?: string | null }>(
  lineItems: ReadonlyArray<T>,
): T | undefined {
  return lineItems.find(
    (li) => DRAY_RE.test(li.item ?? '') && !DRAY_EXCLUDE_RE.test(li.item ?? ''),
  );
}

/**
 * Compare the invoice's base drayage line item rate to the quote's dray rate.
 * Returns both rates when they differ (by ≥ 1¢), else null (matched, no base
 * dray line, or no quote dray rate).
 */
export function detectDrayRateMismatch(
  lineItems: ReadonlyArray<{ item?: string | null; rate?: number | null }>,
  quoteDrayRate: number | null | undefined,
): DrayRateMismatch | null {
  const quoteRate = Number(quoteDrayRate) || 0;
  if (quoteRate <= 0) return null;
  const dray = findBaseDrayItem(lineItems);
  if (!dray) return null;
  const invoiceRate = Number(dray.rate) || 0;
  if (Math.abs(invoiceRate - quoteRate) < 0.01) return null;
  return { invoiceRate, quoteRate };
}

